import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/base/Loading";
import { ADMIN_MODULES, DEFAULT_ROLE_PERMISSIONS, type AdminModule } from "@/lib/adminModules";
import type { PermissionRow } from "@/pages/admin/staff/hooks/useStaff";

interface PermissionMatrixProps {
  permissions: PermissionRow[];
  loading: boolean;
  onSave: (rows: { role: string; module: string; allowed: boolean }[]) => Promise<number>;
  notify: (text: string, tone?: "success" | "error") => void;
}

function buildStaffMap(rows: PermissionRow[]): Record<AdminModule, boolean> {
  const map: Record<AdminModule, boolean> = { ...DEFAULT_ROLE_PERMISSIONS.staff };
  rows
    .filter((row) => row.role === "staff")
    .forEach((row) => {
      if (row.module in map) {
        map[row.module as AdminModule] = row.allowed;
      }
    });
  return map;
}

export default function PermissionMatrix({ permissions, loading, onSave, notify }: PermissionMatrixProps) {
  const saved = useMemo(() => buildStaffMap(permissions), [permissions]);
  const [draft, setDraft] = useState<Record<AdminModule, boolean>>(saved);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(saved);
  }, [saved]);

  const dirty = useMemo(
    () => ADMIN_MODULES.some((module) => draft[module.key] !== saved[module.key]),
    [draft, saved],
  );

  const toggle = (module: AdminModule) => {
    setDraft((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const rows = ADMIN_MODULES.map((module) => ({
        role: "staff",
        module: module.key,
        allowed: draft[module.key],
      }));
      const updated = await onSave(rows);
      notify(`客服权限已更新（${updated} 项）`, "success");
    } catch (err) {
      notify(err instanceof Error ? err.message : "保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-base font-semibold text-foreground-950">权限矩阵</h2>
          <p className="mt-1 text-xs text-foreground-500">
            勾选「客服」可访问的后台模块；超管固定拥有全部权限，不可被关闭，避免把自己锁在门外。
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving || loading}
          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-save-line" />
          </span>
          {saving ? "保存中…" : dirty ? "保存权限" : "已保存"}
        </button>
      </div>

      {loading ? (
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((item) => (
            <Skeleton key={item} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-background-100 text-left text-xs text-foreground-600">
                <th className="px-4 py-3 font-medium">模块</th>
                <th className="w-28 px-4 py-3 text-center font-medium">客服</th>
                <th className="w-28 px-4 py-3 text-center font-medium">超管</th>
              </tr>
            </thead>
            <tbody>
              {ADMIN_MODULES.map((module) => (
                <tr key={module.key} className="border-t border-background-200">
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={`${module.icon} text-base`} />
                      </span>
                      <div>
                        <p className="font-medium text-foreground-950">{module.label}</p>
                        <p className="mt-0.5 text-xs text-foreground-500">{module.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <label className="inline-flex cursor-pointer items-center justify-center">
                      <input
                        type="checkbox"
                        checked={draft[module.key]}
                        onChange={() => toggle(module.key)}
                        className="h-4 w-4 cursor-pointer accent-primary-600"
                        aria-label={`客服可访问 ${module.label}`}
                      />
                    </label>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className="ri-lock-line text-xs" />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}